"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import {
  OneProportionResults,
  TwoProportionResults,
} from "@/components/tools/stats-tests/proportion-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import {
  oneProportionZ,
  twoProportionZ,
} from "@/lib/tool-kit/stats-tests/proportions";

/** English strings. A translated content file overrides these through `ui`. */
export const TWO_PROPORTION_Z_TEST_CALCULATOR_STRINGS = {
  testLabel: "Test",
  oneProportion: "One proportion vs a value",
  twoProportions: "Two independent proportions",
  successesLabel: "Successes (x)",
  trialsLabel: "Sample size (n)",
  p0Label: "Hypothesised proportion (p₀)",
  group1: "Group 1",
  group2: "Group 2",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided (≠)",
  less: "Less than (<)",
  greater: "Greater than (>)",
  alternativeHint: "Direction is p̂ − p₀, or group 1 minus group 2.",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level",
  tryExample: "Try an example",
  exampleOne: "58|100|0.5",
  exampleTwo: "56|70|48|80",
  results: "Results",
  zStatistic: "z statistic",
  pValueFor: "p-value, {alternative}",
  pHat: "Sample proportion p̂",
  pHat1: "p̂₁",
  pHat2: "p̂₂",
  difference: "Difference p̂₁ − p̂₂",
  pooled: "Pooled proportion p̄",
  se0: "Standard error under H₀",
  wilson: "{level} Wilson score CI",
  wald: "{level} Wald CI",
  newcombe: "{level} Newcombe CI for the difference",
  waldDifference: "{level} Wald CI for the difference",
  cohensH: "Cohen's h",
  decisionReject: "Reject H₀ at α = {alpha}: p = {p} is below α.",
  decisionKeep: "Do not reject H₀ at α = {alpha}: p = {p} is not below α.",
  intervalNote:
    "The Wilson and Newcombe intervals keep close to their stated coverage even near 0 or 1 and with small samples; the Wald interval, p̂ ± z × SE, is shown because textbooks use it, but it can be badly off there.",
  smallOne:
    "n × p₀ = {successes} and n × (1 − p₀) = {failures}: both should be at least 10 for the normal approximation. With fewer, use an exact binomial test.",
  smallTwo:
    "The smallest expected count is {min}; with fewer than 5 expected successes or failures in a group, use Fisher's exact test instead.",
  pTableCaption: "p-values for each alternative hypothesis",
  alternativeColumn: "Alternative",
  exactColumn: "p",
  apaColumn: "APA",
  howCalculated: "How this was calculated",
  workingWilson:
    "Wilson: (p̂ + z²/2n ± z√(p̂(1 − p̂)/n + z²/4n²)) / (1 + z²/n), with z = {z}, gives {ci}",
  workingNewcombe:
    "Newcombe: combine the Wilson intervals of p̂₁ and p̂₂ (method 10) to get {ci}",
  apaHeading: "APA-style result",
  apaOne:
    "*z* = {z}, *p* {p}, *h* = {h}, p̂ = {phat}, {level} CI [{low}, {high}]",
  apaTwo:
    "*z* = {z}, *p* {p}, *h* = {h}; difference = {diff}, {level} CI [{low}, {high}]",
  apaNoteOne: "The interval is the Wilson score interval for the proportion.",
  apaNoteTwo:
    "The interval is Newcombe's hybrid score interval for the difference in proportions.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptOne:
    "Enter whole numbers for successes and sample size (successes no larger than n), and p₀ between 0 and 1.",
  promptTwo:
    "Enter successes and sample size for both groups, as whole numbers with successes no larger than n.",
  errorAllSame:
    "Every observation in both groups is a success (or every one a failure), so the pooled standard error is zero and z is undefined.",
} as const;

type Mode = "one" | "two";

interface CountsText {
  x: string;
  n: string;
}

const EMPTY: CountsText = { x: "", n: "" };
const SPLIT = "|";

function readCounts(text: CountsText): { x: number; n: number } | null {
  const x = parseIntegerInput(text.x);
  const n = parseIntegerInput(text.n);
  return x === null || n === null ? null : { x, n };
}

function CountFields({
  prefix,
  title,
  value,
  onChange,
  successesLabel,
  trialsLabel,
}: {
  prefix: string;
  title: string;
  value: CountsText;
  onChange: (next: CountsText) => void;
  successesLabel: string;
  trialsLabel: string;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium text-foreground text-sm">{title}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id={`${prefix}-x`}
          label={successesLabel}
          numeric
          onChange={(x) => onChange({ ...value, x })}
          value={value.x}
        />
        <TextField
          id={`${prefix}-n`}
          label={trialsLabel}
          numeric
          onChange={(n) => onChange({ ...value, n })}
          value={value.n}
        />
      </div>
    </fieldset>
  );
}

export default function TwoProportionZTestCalculatorWidget() {
  const strings = useToolUi(TWO_PROPORTION_Z_TEST_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("two");
  const [group1, setGroup1] = useState<CountsText>(EMPTY);
  const [group2, setGroup2] = useState<CountsText>(EMPTY);
  const [p0, setP0] = useState("0.5");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const first = readCounts(group1);
  const second = readCounts(group2);
  const hypothesis = parseNumberInput(p0);
  const one =
    mode === "one" && first && hypothesis !== null
      ? oneProportionZ(first.x, first.n, hypothesis, Number(level))
      : null;
  const two =
    mode === "two" && first && second
      ? twoProportionZ(first.x, first.n, second.x, second.n, Number(level))
      : null;
  const bothValid =
    first && second && first.x <= first.n && second.x <= second.n;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  function fillExample() {
    if (mode === "one") {
      const [x = "", n = "", value = ""] = strings.exampleOne.split(SPLIT);
      setGroup1({ x, n });
      setP0(value);
      return;
    }
    const [x1 = "", n1 = "", x2 = "", n2 = ""] =
      strings.exampleTwo.split(SPLIT);
    setGroup1({ x: x1, n: n1 });
    setGroup2({ x: x2, n: n2 });
  }

  let notice: string = mode === "one" ? strings.promptOne : strings.promptTwo;
  if (mode === "two" && two === null && bothValid) {
    notice = strings.errorAllSame;
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.testLabel}
        name="prop-mode"
        onChange={setMode}
        options={[
          { value: "one", label: strings.oneProportion },
          { value: "two", label: strings.twoProportions },
        ]}
        value={mode}
      />
      <div className="grid gap-6 sm:grid-cols-2">
        <CountFields
          onChange={setGroup1}
          prefix="prop-1"
          successesLabel={strings.successesLabel}
          title={strings.group1}
          trialsLabel={strings.trialsLabel}
          value={group1}
        />
        {mode === "two" ? (
          <CountFields
            onChange={setGroup2}
            prefix="prop-2"
            successesLabel={strings.successesLabel}
            title={strings.group2}
            trialsLabel={strings.trialsLabel}
            value={group2}
          />
        ) : (
          <TextField
            id="prop-p0"
            label={strings.p0Label}
            numeric
            onChange={setP0}
            value={p0}
          />
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          hint={strings.alternativeHint}
          id="prop-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="prop-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="prop-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={fillExample} />
      {one ? (
        <OneProportionResults
          alpha={Number(alpha)}
          alternative={alternative}
          fmt={fmt}
          labels={alternatives}
          r={one}
          strings={strings}
        />
      ) : null}
      {two ? (
        <TwoProportionResults
          alpha={Number(alpha)}
          alternative={alternative}
          fmt={fmt}
          labels={alternatives}
          r={two}
          strings={strings}
        />
      ) : null}
      {one || two ? null : <Notice>{notice}</Notice>}
    </div>
  );
}
